export type DashboardHeatmapScale = "quantile";

export type DashboardHeatmapOptions = {
    bucketMs: number;
    cellCount: number;
    scale: DashboardHeatmapScale;
};

export const DEFAULT_DASHBOARD_HEATMAP_OPTIONS: Readonly<DashboardHeatmapOptions> = Object.freeze({
    bucketMs   : 24 * 60 * 60_000,
    cellCount  : 28,
    scale      : "quantile",
});

const DAYS_PER_WEEK = 7;

export function resolveDashboardHeatmapWindowBefore(now: number, bucketMs: number) {
    if (bucketMs !== DEFAULT_DASHBOARD_HEATMAP_OPTIONS.bucketMs)
        return Math.ceil(now / bucketMs) * bucketMs;

    const startOfDay = Math.floor(now / bucketMs) * bucketMs;
    const weekdayFromMonday = (new Date(startOfDay).getUTCDay() + 6) % DAYS_PER_WEEK;

    if (now === startOfDay && weekdayFromMonday === 0)
        return now;

    return startOfDay - weekdayFromMonday * bucketMs + DAYS_PER_WEEK * bucketMs;
}

const QuantileIntensities = [ .18, .38, .58, .78, 1 ] as const;

export function resolveDashboardHeatmapOptions(options?: Partial<DashboardHeatmapOptions> | null): DashboardHeatmapOptions {
    return {
        bucketMs   : options?.bucketMs ?? DEFAULT_DASHBOARD_HEATMAP_OPTIONS.bucketMs,
        cellCount  : options?.cellCount ?? DEFAULT_DASHBOARD_HEATMAP_OPTIONS.cellCount,
        scale      : options?.scale ?? DEFAULT_DASHBOARD_HEATMAP_OPTIONS.scale,
    };
}

export function normalizeDashboardHeatmapValues(values: number[], cellCount: number) {
    const normalizedCellCount = Math.max(1, Math.floor(cellCount));
    const recentValues = values.slice(-normalizedCellCount);

    return [
        ...Array.from({ length: normalizedCellCount - recentValues.length }, () => 0),
        ...recentValues,
    ];
}

export function buildDashboardHeatmapIntensities(values: number[], scale: DashboardHeatmapScale): number[] {
    if (scale !== "quantile")
        return values.map((value) => value > 0 ? QuantileIntensities[2] : 0);

    const sortedPositiveValues = values
        .filter((value) => value > 0)
        .sort((left, right) => left - right);

    if (!sortedPositiveValues.length)
        return values.map(() => 0);

    const minimum = sortedPositiveValues[0]!;
    const maximum = sortedPositiveValues.at(-1)!;

    if (minimum === maximum)
        return values.map((value) => value > 0 ? QuantileIntensities[2] : 0);

    return values.map((value) => {
        if (value <= 0)
            return 0;

        const firstIndex = sortedPositiveValues.indexOf(value);
        const lastIndex = sortedPositiveValues.lastIndexOf(value);
        const rank = ((firstIndex + lastIndex) / 2) / (sortedPositiveValues.length - 1);
        const levelIndex = Math.min(
            QuantileIntensities.length - 1,
            Math.floor(rank * QuantileIntensities.length),
        );

        return QuantileIntensities[levelIndex]!;
    });
}
